/** Classic proposal (merchant view) driven by the pricing engine: brand header, analysis, option cards, bottom line, equipment, apply. */
import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { CheckCircle2, ArrowRight, CreditCard, Wrench, Users, LineChart } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const money = (n, d = 0) => `${n < 0 ? '-' : ''}$${Math.abs(n || 0).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })}`;
const BENEFITS = {
  dual_pricing: ['Card customers cover the card service fee', 'Compliant signage and receipts included', 'Set up automatically at your terminal'],
  interchange_plus: ['You pay the true card brand cost plus a fixed markup', 'Every fee is shown on your statement', 'Lower cost on debit cards'],
  flat_rate: ['One simple rate every month', 'Easy to reconcile', 'Predictable for small tickets'],
};

function Header({ brand, color }) {
  return (
    <header className="sticky top-0 z-50 text-white shadow-lg" style={{ backgroundColor: color }} data-testid="classic-header">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-3">
          {brand.logo_url && <img src={brand.logo_url} alt="" className="h-8 w-auto object-contain" />}
          <span className="text-lg font-bold" data-testid="classic-brand-name">{brand.company_name || 'Merchant Solutions Corp'}</span>
        </div>
        <a href="#classic-cta" className="rounded-lg bg-white px-5 py-2 text-sm font-bold transition-opacity hover:opacity-90" style={{ color }} data-testid="classic-header-cta">Get Started</a>
      </div>
    </header>
  );
}

function Metric({ label, value, good, testid }) {
  return (
    <div className={`rounded-xl bg-white p-5 shadow-sm ${good ? 'ring-1 ring-emerald-200' : ''}`}>
      <p className={`mb-1 text-xs ${good ? 'font-semibold text-emerald-600' : 'text-gray-400'}`}>{label}</p>
      <p className={`text-xl font-extrabold ${good ? 'text-emerald-600' : 'text-gray-800'}`} data-testid={testid}>{value}</p>
    </div>
  );
}

function Hero({ p, q, rec, color }) {
  const vol = q.monthly_volume;
  const curRate = vol ? (q.current_monthly_cost / vol) * 100 : 0;
  const newRate = vol ? (rec.proposed_total / vol) * 100 : 0;
  const items = [[CreditCard, 'Payment processing', 'Clear, written pricing.'], [Wrench, 'Installation', 'We set up your equipment.'], [Users, 'Training', 'Your team learns it on day one.'], [LineChart, 'Ongoing reviews', 'We check your statements with you.']];
  return (
    <section className="bg-white py-14 lg:py-20">
      <div className="mx-auto grid max-w-6xl gap-12 px-4 sm:px-6 lg:grid-cols-2">
        <div>
          <p className="mb-5 text-xs font-semibold uppercase tracking-[0.25em] text-gray-400">Proposal prepared for {p.contact_name || p.business_name}</p>
          <h1 className="mb-5 text-3xl font-extrabold leading-tight sm:text-4xl" style={{ color }} data-testid="classic-hero-title">A lower, clearer cost for {p.business_name}</h1>
          <p className="mb-10 text-base leading-relaxed text-gray-500" data-testid="classic-opening">{p.engine.opening}</p>
          <div className="grid grid-cols-2 gap-6">{items.map(([Icon, t, d]) => (
            <div key={t} className="flex gap-3"><Icon className="mt-0.5 h-5 w-5 shrink-0" style={{ color }} /><div><p className="text-sm font-bold" style={{ color }}>{t}</p><p className="text-xs text-gray-400">{d}</p></div></div>))}</div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-7" data-testid="classic-overview">
          <p className="mb-5 text-[11px] font-bold uppercase tracking-[0.2em] text-gray-400">Summary</p>
          <div className="grid grid-cols-2 gap-3">
            <Metric label="Monthly card volume" value={money(vol)} testid="classic-volume" />
            <Metric label="Today's effective rate" value={`${curRate.toFixed(2)}%`} testid="classic-current-rate" />
            <Metric label="Estimated monthly savings" value={money(rec.monthly_savings)} good={rec.monthly_savings > 0} testid="classic-monthly-savings" />
            <Metric label="Proposed effective rate" value={`${newRate.toFixed(2)}%`} good testid="classic-new-rate" />
          </div>
        </div>
      </div>
    </section>
  );
}

function Analysis({ q }) {
  const brandCost = Math.min(q.current_monthly_cost, q.monthly_volume * 0.018);
  const pie = [{ name: 'Card brand cost (estimated)', value: brandCost, color: '#dc2626', desc: 'Set by Visa, Mastercard and the card banks' },
    { name: 'Processor markup and fees', value: Math.max(0, q.current_monthly_cost - brandCost), color: '#f472b6', desc: 'What your current processor adds on top' }];
  return (
    <section className="bg-gray-50 py-14 lg:py-20" data-testid="classic-analysis">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <h2 className="mb-2 text-2xl font-extrabold text-gray-900 sm:text-3xl">What we found</h2>
        <p className="mb-10 text-gray-500">From the statement you shared with us.</p>
        <div className="grid gap-8 lg:grid-cols-2">
          <div className="rounded-2xl border border-gray-100 bg-white p-7 shadow-sm">
            {[['Monthly volume', money(q.monthly_volume, 2)], ['Total fees paid', money(q.current_monthly_cost, 2)], ['Effective rate', `${q.monthly_volume ? ((q.current_monthly_cost / q.monthly_volume) * 100).toFixed(2) : '0.00'}%`]].map(([l, v]) => (
              <div key={l} className="flex justify-between border-b border-gray-100 py-4 last:border-0"><span className="text-sm text-gray-500">{l}</span><span className="text-xl font-bold text-gray-900">{v}</span></div>))}
          </div>
          <div className="flex items-center gap-8 rounded-2xl border border-gray-100 bg-white p-7 shadow-sm" data-testid="classic-fee-chart">
            <div className="h-40 w-40 shrink-0"><ResponsiveContainer><PieChart><Pie data={pie} dataKey="value" innerRadius={44} outerRadius={68} stroke="none">{pie.map((x) => <Cell key={x.name} fill={x.color} />)}</Pie></PieChart></ResponsiveContainer></div>
            <div className="space-y-4">{pie.map((x) => (<div key={x.name}><p className="text-xs font-bold uppercase text-gray-700">{x.name}</p><p className="text-sm font-bold">{money(x.value, 2)}</p><p className="text-[11px] text-gray-400">{x.desc}</p></div>))}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function OptionCard({ k, o, recommended, color }) {
  return (
    <div className={`relative rounded-2xl border-2 p-7 transition-shadow ${recommended ? 'shadow-xl' : 'border-gray-200 hover:shadow-md'}`} style={recommended ? { borderColor: color } : {}} data-testid={`classic-option-${k}`}>
      {recommended && <span className="absolute -top-3 right-4 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white" style={{ backgroundColor: color }} data-testid="classic-recommended-badge">Recommended</span>}
      <h3 className="text-xl font-extrabold text-gray-900">{o.label}</h3>
      <p className="mt-2 text-sm text-gray-500" data-testid={`classic-rate-${k}`}>{o.rate}</p>
      <div className="mt-5 space-y-1 border-t border-gray-100 pt-4 text-sm">
        <div className="flex justify-between"><span className="text-gray-500">Processing</span><span>{money(o.processing, 2)}</span></div>
        <div className="flex justify-between"><span className="text-gray-500">Software and monthly fees</span><span>{money(o.software_and_fees, 2)}</span></div>
        <div className="flex justify-between font-semibold"><span>You pay per month</span><span data-testid={`classic-total-${k}`}>{money(o.proposed_total, 2)}</span></div>
        <p className={`pt-1 font-semibold ${o.monthly_savings >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid={`classic-savings-${k}`}>{o.monthly_savings >= 0 ? `Saves ${money(o.monthly_savings)}/mo` : `Costs ${money(-o.monthly_savings)}/mo more`}</p>
      </div>
      <ul className="mt-5 space-y-2">{(BENEFITS[k] || []).map((b) => <li key={b} className="flex gap-2 text-sm text-gray-700"><CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />{b}</li>)}</ul>
    </div>
  );
}

function Equipment({ eq }) {
  return (
    <section className="bg-white py-14" data-testid="classic-equipment">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <h2 className="mb-6 text-2xl font-extrabold text-gray-900 sm:text-3xl">Your equipment</h2>
        <div className="rounded-2xl border border-gray-200 p-7">
          {eq.logo_url && <img src={eq.logo_url} alt={eq.name} className="mb-3 h-10" />}
          <p className="text-lg font-bold text-gray-900">{eq.name}</p>
          {eq.free ? (<ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-gray-600" data-testid="classic-equipment-terms">
            <li>No upfront cost while monthly volume stays above {money(eq.terms.min_monthly_volume)}.</li>
            <li>Owned by {eq.terms.owner}; {eq.terms.term_months}-month term.</li>
            <li>Cancelling early costs {money(eq.terms.cancellation_fee)}; return the equipment within {eq.terms.return_days} days.</li>
            <li>{eq.terms.below_threshold}</li></ul>) : <p className="mt-2 text-sm text-gray-600" data-testid="classic-equipment-terms">One-time cost {money(eq.upfront_due, 2)}{eq.monthly ? ` · ${money(eq.monthly, 2)}/mo software` : ''}.</p>}
        </div>
      </div>
    </section>
  );
}

function Cta({ e, eq, rec, accepted, noLink, onGo, color }) {
  return (
    <section id="classic-cta" className="py-16 text-center text-white" style={{ backgroundColor: color }} data-testid="classic-cta">
      <div className="mx-auto max-w-3xl px-4">
        <p className="mb-6 text-sm text-white/70">{eq?.free ? `${eq.terms.term_months}-month term tied to the free equipment.` : 'No long-term contract required for processing.'} Pricing is an estimate from your statement; final pricing is confirmed in your application.</p>
        {e.deadline && <p className="mb-6 font-semibold" data-testid="classic-deadline">This offer is available until {new Date(`${e.deadline}T12:00:00`).toLocaleDateString()}.</p>}
        {accepted && <p className="mb-4 font-semibold text-emerald-300" data-testid="classic-accepted">{noLink ? 'Thank you! Your rep has been notified and will send your application today.' : 'Thank you — opening your application…'}</p>}
        <h2 className="mb-8 text-3xl font-extrabold">{rec.monthly_savings > 0 ? `Ready to save ${money(rec.monthly_savings)} a month?` : 'Ready to get started?'}</h2>
        <button type="button" onClick={onGo} className="inline-flex items-center gap-3 rounded-xl bg-emerald-500 px-10 py-4 text-lg font-bold text-white shadow-2xl transition-transform hover:scale-[1.03] hover:bg-emerald-400" data-testid="classic-start-application">
          {accepted ? 'Continue my application' : 'Start my application'} <ArrowRight className="h-5 w-5" /></button>
      </div>
    </section>
  );
}

export default function ClassicEngineView({ p, accepted, onAccept }) {
  const [noLink, setNoLink] = useState(false);
  const go = async () => { const r = await onAccept(); if (r && !r.application_url) setNoLink(true); };
  const e = p.engine;
  const q = e.quote;
  const rec = q.options[e.recommended] || Object.values(q.options)[0];
  const brand = p.tenant_branding || {};
  const color = brand.primary_color || '#1a1f4e';
  return (
    <div className="min-h-screen bg-white" data-testid="classic-proposal">
      <Header brand={brand} color={color} />
      {p.cover_image_url && <img src={`${API}${p.cover_image_url}`} alt="" className="max-h-[380px] w-full object-cover" data-testid="proposal-cover-image" />}
      <Hero p={p} q={q} rec={rec} color={color} />
      <Analysis q={q} />
      <section className="bg-white py-14 lg:py-20" data-testid="classic-options">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2 className="mb-2 text-2xl font-extrabold text-gray-900 sm:text-3xl">Your options</h2>
          <p className="mb-10 text-gray-500">Monthly figures based on your current volume.</p>
          <div className={`grid gap-6 ${Object.keys(q.options).length > 2 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
            {Object.entries(q.options).map(([k, o]) => <OptionCard key={k} k={k} o={o} recommended={k === e.recommended} color={color} />)}
          </div>
        </div>
      </section>
      {rec.annual_savings > 0 && <section className="bg-[#0f5132] py-14 text-white" data-testid="classic-bottom-line">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 sm:px-6 md:grid-cols-2">
          <div className="space-y-6"><div><p className="text-sm uppercase text-white/50">Today per month</p><p className="text-3xl font-bold">{money(q.current_monthly_cost, 2)}</p></div>
            <div><p className="text-sm uppercase text-white/50">With {rec.label}</p><p className="text-3xl font-bold">{money(rec.proposed_total, 2)}</p></div></div>
          <div className="md:text-right"><p className="text-xs uppercase tracking-[0.2em] text-white/50">Estimated annual savings</p>
            <p className="text-5xl font-black" data-testid="classic-annual-savings">{money(rec.annual_savings)}</p>
            <p className="mt-3 text-white/60">First year net {money(rec.first_year_net)} after upfront costs.</p>
            <p className="mt-3 text-sm text-white/60" data-testid="classic-consequence">{e.consequence}</p></div>
        </div>
      </section>}
      {q.equipment && <Equipment eq={q.equipment} />}
      <Cta e={e} eq={q.equipment} rec={rec} accepted={accepted} noLink={noLink} onGo={go} color={color} />
      <footer className="py-6 text-center text-xs text-gray-400">Proposal {p.proposal_id} · prepared for {p.business_name}</footer>
    </div>
  );
}
